import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

// Phase 71-05 (TRIAL-05) — shared client-advisory consumed-probe for the
// "première séance" product, extracted from ReserveTrial so the Dashboard CTA
// reuses the exact same status semantics (DRY — single source of the 3-way
// classification).
//
// Owner-scoped SELECT on bookings. The `.eq('user_id', userId)` is load-bearing:
// RLS bookings_select_own is `((user_id = auth.uid()) OR is_admin())`, so for a
// staff account it scopes to nothing and "the latest premiere-seance booking"
// would be ANY user's. The latest non-cancelled/failed row of THIS user is then
// classified into THREE states:
//   - consumed     (confirmed/completed/no_show) → première already used up
//   - in_progress  (held + held_until > now)      → a payment is pending
//   - available    (no row / expired held)        → free to book
//
// This is UX only. Authoritative once-per-user enforcement is server-side:
// create-booking-checkout 422 (71-02) + confirm_booking reject + the
// confirm_native_booking_paid RPC gate (71-01). Never rely on this probe.
export type PremiereState = 'consumed' | 'in_progress' | 'available'

const CONSUMED_STATUSES = new Set(['confirmed', 'completed', 'no_show'])

export interface PremiereStatus {
  loading: boolean
  state: PremiereState
}

export function usePremiereStatus(userId: string | undefined | null): PremiereStatus {
  const [loading, setLoading] = useState(true)
  const [state, setState] = useState<PremiereState>('available')

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    setLoading(true)
    ;(async () => {
      const { data } = await supabase
        .from('bookings')
        .select('id,status,held_until')
        .eq('user_id', userId)
        .eq('event_type_slug', 'premiere-seance')
        // Mirror the 71-01 RPC once-per-user filter exactly.
        .not('status', 'in', '(cancelled,failed,failed_promo_violation)')
        .order('held_until', { ascending: false })
        .limit(1)
      if (cancelled) return
      const row = (data as { status: string; held_until: string | null }[] | null)?.[0]
      if (row && CONSUMED_STATUSES.has(row.status)) {
        setState('consumed')
      } else if (
        row &&
        row.status === 'held' &&
        row.held_until &&
        new Date(row.held_until).getTime() > Date.now()
      ) {
        setState('in_progress')
      } else {
        setState('available')
      }
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [userId])

  return { loading, state }
}
